import { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Loader2,
    LogIn,
    Mail,
    KeyRound,
    CheckCircle2,
    ArrowLeft,
    ShieldCheck,
    Users,
    Clock,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Redirect the browser to Emergent Google Auth.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 * The redirect target MUST be derived from `window.location.origin` so that
 * the OAuth callback returns to the exact domain the user is on right now.
 */
function startGoogleLogin(entryRole) {
    try { sessionStorage.setItem("ss_login_entry", entryRole || "employee"); } catch (e) { console.warn("sessionStorage non disponibile:", e?.message); }
    const redirectUrl = window.location.origin + "/";
    window.location.href =
        "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

/** Reusable "Continue with Google" divider + button. */
function GoogleAuthButton({ testId = "google-login-button", label = "Accedi con Google", entryRole = "employee" }) {
    return (
        <>
            <div className="relative flex items-center py-1">
                <div className="flex-grow border-t border-slate-200" />
                <span className="mx-3 text-[10px] font-medium uppercase tracking-widest text-slate-400">oppure</span>
                <div className="flex-grow border-t border-slate-200" />
            </div>
            <button
                type="button"
                onClick={() => startGoogleLogin(entryRole)}
                data-testid={testId}
                className="flex w-full items-center justify-center gap-2.5 rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#376DB3]/40"
            >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                <span>{label}</span>
            </button>
        </>
    );
}

export default function Login() {
    const { user, login, loading } = useAuth();
    const [params] = useSearchParams();
    const roleParam = params.get("role");
    const role = roleParam === "admin" ? "admin" : roleParam === "timbratura" ? "timbratura" : "employee";
    const isAdmin = role === "admin";
    const isTimbratura = role === "timbratura";
    const [presenzeCfg, setPresenzeCfg] = useState(null);
    useEffect(() => {
        if (!isTimbratura) return;
        api.get("/presenze/config")
            .then((r) => setPresenzeCfg(r.data))
            .catch(() => setPresenzeCfg({ enabled: false }));
    }, [isTimbratura]);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [forgotOpen, setForgotOpen] = useState(false);

    if (!loading && user) {
        // Honor the chosen entry point: an operator ("amministratore" manager)
        // logging in from the EMPLOYEE entry must land on their employee portal,
        // and on the gestionale when entering from the ADMIN entry.
        let target;
        if (isTimbratura) {
            target = user.role === "admin" ? "/" : "/timbratura";
        } else if (isAdmin) {
            target = user.role === "employee" ? "/portale" : "/";
        } else {
            target = user.role === "admin" ? "/" : "/portale";
        }
        return <Navigate to={target} replace />;
    }

    // Timbratura entry is gated by the module flag (admin switch in /presenze)
    if (isTimbratura) {
        if (presenzeCfg === null) {
            return (
                <div className="flex h-screen items-center justify-center" data-testid="timbratura-cfg-loading">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                </div>
            );
        }
        if (!presenzeCfg.enabled) {
            return <Navigate to="/accesso" replace />;
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const result = await login(email.trim(), password);
        setSubmitting(false);
        if (!result.ok) {
            setError(result.error);
            toast.error(result.error);
            return;
        }
    };

    // Render the appropriate variant
    return isAdmin ? (
        <AdminLoginLayout
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            submitting={submitting}
            error={error}
            onSubmit={handleSubmit}
            onForgot={() => setForgotOpen(true)}
            forgotOpen={forgotOpen}
            onCloseForgot={() => setForgotOpen(false)}
        />
    ) : isTimbratura ? (
        <TimbraturaLoginLayout
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            submitting={submitting}
            error={error}
            onSubmit={handleSubmit}
            onForgot={() => setForgotOpen(true)}
            forgotOpen={forgotOpen}
            onCloseForgot={() => setForgotOpen(false)}
        />
    ) : (
        <EmployeeLoginLayout
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            submitting={submitting}
            error={error}
            onSubmit={handleSubmit}
            onForgot={() => setForgotOpen(true)}
            forgotOpen={forgotOpen}
            onCloseForgot={() => setForgotOpen(false)}
        />
    );
}


function EmployeeLoginLayout({
    email, setEmail, password, setPassword, submitting, error,
    onSubmit, onForgot, forgotOpen, onCloseForgot,
}) {
    return (
        <div className="relative min-h-screen overflow-hidden" data-testid="login-page" data-role="employee">
            {/* Background image + overlay */}
            <img
                src="/brand/login-bg.webp"
                alt="Servizi di pulizia professionale"
                className="absolute inset-0 h-full w-full object-cover brightness-110 saturate-75"
            />
            <div className="absolute inset-0 bg-[#376DB3]/65" />

            {/* Top bar */}
            <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-4 py-4 sm:px-8">
                <Link
                    to="/accesso"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-white/25"
                    data-testid="change-role-link"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Cambia ruolo
                </Link>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur">
                    <Users className="h-3.5 w-3.5" />
                    Dipendente
                </div>
            </div>

            {/* Centered card */}
            <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl sm:p-10" data-testid="login-card">
                    <div className="flex justify-center">
                        <img
                            src="/brand/logo-full.jpg"
                            alt="Splendor Sud"
                            width={301}
                            height={112}
                            className="h-28 w-auto"
                            style={{ aspectRatio: "1812 / 674" }}
                        />
                    </div>

                    <div className="mt-6 text-center">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#376DB3]/70">
                            Area Personale
                        </div>
                        <h1 className="mt-1 font-display text-2xl font-bold text-[#376DB3] sm:text-3xl">
                            Portale HR
                        </h1>
                        <p className="mt-2 text-sm text-slate-600">
                            Accedi per gestire i documenti del tuo rapporto di lavoro
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                                Email o username
                            </Label>
                            <Input
                                id="email"
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="nome@splendorsud.it oppure il tuo username"
                                required
                                autoFocus
                                autoComplete="username"
                                className="mt-1.5 border-slate-300 bg-slate-50 focus-visible:border-[#376DB3] focus-visible:bg-white"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                autoComplete="current-password"
                                className="mt-1.5 border-slate-300 bg-slate-50 focus-visible:border-[#376DB3] focus-visible:bg-white"
                                data-testid="login-password"
                            />
                        </div>

                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full gap-2 bg-[#376DB3] py-6 text-base font-medium text-white shadow-md hover:bg-[#376DB3]/90"
                            data-testid="login-submit"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                            Accedi
                        </Button>

                        <GoogleAuthButton testId="google-login-employee" entryRole="employee" />

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={onForgot}
                                className="text-sm font-medium text-[#376DB3] hover:underline"
                                data-testid="forgot-password-link"
                            >
                                Password dimenticata?
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 text-center text-xs text-slate-500">
                        Splendor Sud S.r.l. · Via Abruzzo, 1 · 74121 Taranto
                        <div className="mt-1">
                            Per assistenza e comunicazioni:{" "}
                            <a
                                href="mailto:dipendenti@splendorsud.com"
                                className="font-medium text-[#376DB3] hover:underline"
                                data-testid="login-support-email"
                            >
                                dipendenti@splendorsud.com
                            </a>
                        </div>
                        <div className="mt-1">
                            Tel.{" "}
                            <a href="tel:+39099375014" className="font-medium text-[#376DB3] hover:underline" data-testid="login-support-tel">099.375014</a>
                            {" · "}
                            Cell.{" "}
                            <a href="tel:+393481966463" className="font-medium text-[#376DB3] hover:underline" data-testid="login-support-cell">348.1966463</a>
                        </div>
                    </div>

                    <div className="mt-5 border-t border-slate-100 pt-4 text-center">
                        <Link
                            to="/accesso"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#376DB3] hover:underline"
                            data-testid="login-back-home"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Torna alla pagina iniziale
                        </Link>
                    </div>
                </div>
            </div>

            <ForgotPasswordDialog open={forgotOpen} onClose={onCloseForgot} variant="employee" />
        </div>
    );
}


function TimbraturaLoginLayout({
    email, setEmail, password, setPassword, submitting, error,
    onSubmit, onForgot, forgotOpen, onCloseForgot,
}) {
    return (
        <div className="relative min-h-screen overflow-hidden" data-testid="login-page" data-role="timbratura">
            <img
                src="/brand/login-bg-timbratura.webp"
                alt="Timbratura geolocalizzata da smartphone"
                className="absolute inset-0 h-full w-full object-cover brightness-105 saturate-[0.35]"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#15498F]/75 via-[#376DB3]/65 to-[#5BAEDB]/55" />

            {/* Top bar */}
            <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-4 py-4 sm:px-8">
                <Link
                    to="/accesso"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-white/25"
                    data-testid="change-role-link"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Cambia ruolo
                </Link>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur">
                    <Clock className="h-3.5 w-3.5" />
                    Timbratura
                </div>
            </div>

            {/* Centered card */}
            <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl sm:p-10" data-testid="login-card">
                    <div className="flex justify-center">
                        <img
                            src="/brand/logo-full.jpg"
                            alt="Splendor Sud"
                            width={301}
                            height={112}
                            className="h-28 w-auto"
                            style={{ aspectRatio: "1812 / 674" }}
                        />
                    </div>

                    <div className="mt-6 text-center">
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-[#5BAEDB]/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#376DB3] ring-1 ring-[#5BAEDB]/30">
                            <Clock className="h-3 w-3" />
                            Presenze
                        </div>
                        <h1 className="mt-3 font-display text-2xl font-bold text-[#376DB3] sm:text-3xl">
                            Timbratura
                        </h1>
                        <p className="mt-2 text-sm text-slate-600">
                            Accedi con le credenziali del portale per registrare entrata e uscita
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5">
                        <div>
                            <Label htmlFor="timb-email" className="text-sm font-medium text-slate-700">
                                Email o username
                            </Label>
                            <Input
                                id="timb-email"
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="nome@splendorsud.it oppure il tuo username"
                                required
                                autoFocus
                                autoComplete="username"
                                className="mt-1.5 border-slate-300 bg-slate-50 focus-visible:border-[#376DB3] focus-visible:bg-white"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="timb-password" className="text-sm font-medium text-slate-700">
                                Password
                            </Label>
                            <Input
                                id="timb-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                autoComplete="current-password"
                                className="mt-1.5 border-slate-300 bg-slate-50 focus-visible:border-[#376DB3] focus-visible:bg-white"
                                data-testid="login-password"
                            />
                        </div>

                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full gap-2 bg-[#376DB3] py-6 text-base font-medium text-white shadow-md hover:bg-[#376DB3]/90"
                            data-testid="login-submit"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                            Vai alla timbratura
                        </Button>

                        <GoogleAuthButton testId="google-login-timbratura" entryRole="timbratura" />

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={onForgot}
                                className="text-sm font-medium text-[#376DB3] hover:underline"
                                data-testid="forgot-password-link"
                            >
                                Password dimenticata?
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 text-center text-xs text-slate-500">
                        Per assistenza:{" "}
                        <a
                            href="mailto:dipendenti@splendorsud.com"
                            className="font-medium text-[#376DB3] hover:underline"
                        >
                            dipendenti@splendorsud.com
                        </a>
                    </div>

                    <div className="mt-5 border-t border-slate-100 pt-4 text-center">
                        <Link
                            to="/accesso"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#376DB3] hover:underline"
                            data-testid="login-back-home-timbratura"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Torna alla pagina iniziale
                        </Link>
                    </div>
                </div>
            </div>

            <ForgotPasswordDialog open={forgotOpen} onClose={onCloseForgot} variant="employee" />
        </div>
    );
}


function AdminLoginLayout({
    email, setEmail, password, setPassword, submitting, error,
    onSubmit, onForgot, forgotOpen, onCloseForgot,
}) {
    return (
        <div
            className="relative min-h-screen overflow-hidden"
            data-testid="login-page"
            data-role="admin"
        >
            {/* Background image */}
            <img
                src="/brand/login-bg-admin.webp"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
            />
            {/* Brand blue overlay to keep readability + lighten further */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#376DB3]/55 via-[#5BAEDB]/45 to-white/35" />
            <div className="absolute inset-0 bg-white/15 backdrop-blur-[1px]" />

            {/* Top bar */}
            <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-4 py-4 sm:px-8">
                <Link
                    to="/accesso"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1.5 text-xs font-medium text-[#15498F] shadow-sm backdrop-blur transition-colors hover:bg-white"
                    data-testid="change-role-link"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Cambia ruolo
                </Link>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1.5 text-xs font-medium text-[#15498F] shadow-sm ring-1 ring-white/40 backdrop-blur">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Amministratore
                </div>
            </div>

            {/* Centered card */}
            <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
                <div
                    className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl sm:p-10"
                    data-testid="login-card"
                >
                    <div className="flex justify-center">
                        <img
                            src="/brand/logo-full.jpg"
                            alt="Splendor Sud"
                            width={258}
                            height={96}
                            className="h-24 w-auto"
                            style={{ aspectRatio: "1812 / 674" }}
                        />
                    </div>

                    <div className="mt-6 text-center">
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-[#5BAEDB]/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#15498F] ring-1 ring-[#5BAEDB]/30">
                            <ShieldCheck className="h-3 w-3" />
                            Pannello di Controllo
                        </div>
                        <h1 className="mt-3 font-display text-2xl font-bold text-[#15498F] sm:text-3xl">
                            Gestionale HR
                        </h1>
                        <p className="mt-2 text-sm text-slate-600">
                            Accesso riservato agli amministratori del personale
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5">
                        <div>
                            <Label htmlFor="admin-email" className="text-sm font-medium text-slate-700">
                                Email amministratore
                            </Label>
                            <Input
                                id="admin-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@splendorsud.it"
                                required
                                autoFocus
                                autoComplete="email"
                                className="mt-1.5 border-slate-300 bg-slate-50 focus-visible:border-[#15498F] focus-visible:bg-white"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="admin-password" className="text-sm font-medium text-slate-700">
                                Password
                            </Label>
                            <Input
                                id="admin-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                autoComplete="current-password"
                                className="mt-1.5 border-slate-300 bg-slate-50 focus-visible:border-[#15498F] focus-visible:bg-white"
                                data-testid="login-password"
                            />
                        </div>

                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full gap-2 bg-[#15498F] py-6 text-base font-medium text-white shadow-md hover:bg-[#15498F]/90"
                            data-testid="login-submit"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                            Accedi al Gestionale
                        </Button>

                        <GoogleAuthButton testId="google-login-admin" entryRole="admin" />

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={onForgot}
                                className="text-sm font-medium text-[#376DB3] hover:underline"
                                data-testid="forgot-password-link"
                            >
                                Password dimenticata?
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 border-t border-slate-200 pt-5 text-center text-xs text-slate-500">
                        Sei un dipendente?{" "}
                        <Link
                            to="/login?role=employee"
                            className="font-medium text-[#376DB3] hover:underline"
                            data-testid="switch-to-employee"
                        >
                            Vai al Portale HR
                        </Link>
                    </div>

                    <div className="mt-4 text-center">
                        <Link
                            to="/accesso"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#376DB3] hover:underline"
                            data-testid="login-back-home-admin"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Torna alla pagina iniziale
                        </Link>
                    </div>
                </div>
            </div>

            <ForgotPasswordDialog open={forgotOpen} onClose={onCloseForgot} variant="admin" />
        </div>
    );
}


function ForgotPasswordDialog({ open, onClose, variant = "employee" }) {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const accent = variant === "admin" ? "text-[#15498F]" : "text-[#376DB3]";
    const btn = variant === "admin"
        ? "bg-[#15498F] text-white hover:bg-[#15498F]/90"
        : "bg-[#376DB3] text-white hover:bg-[#376DB3]/90";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
            setResult({ message: data.message, sent: data.sent });
            toast.success("Richiesta inviata");
        } catch (e) {
            toast.error("Errore invio richiesta");
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setEmail("");
        setResult(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <KeyRound className={`h-5 w-5 ${accent}`} />
                        Recupera password
                    </DialogTitle>
                    <DialogDescription>
                        Inserisci la tua email aziendale. Ti invieremo le istruzioni per impostare una nuova password.
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    <div className="space-y-4">
                        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                            <div data-testid="forgot-result">
                                <p className="font-medium">{result.message}</p>
                                {!result.sent && (
                                    <p className="mt-1 text-xs text-emerald-700">
                                        L'invio email automatico non è ancora attivo. Contatta l'amministratore HR
                                        per ricevere il link di reset.
                                    </p>
                                )}
                            </div>
                        </div>
                        <Button onClick={handleClose} className={`w-full ${btn}`} data-testid="forgot-close-btn">
                            Chiudi
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="forgot-email">Email</Label>
                            <div className="relative mt-1.5">
                                <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <Input
                                    id="forgot-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="nome@splendorsud.it"
                                    required
                                    autoFocus
                                    className="pl-8"
                                    data-testid="forgot-email-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={handleClose} data-testid="forgot-cancel-btn">Annulla</Button>
                            <Button
                                type="submit"
                                disabled={submitting || !email}
                                className={`gap-2 ${btn}`}
                                data-testid="forgot-submit-btn"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                Invia link di reset
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
